import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { hasRouteAccess, RequiredRole } from '../../auth/roles';
import { paths } from 'shared/routing/paths';

interface PrivateRouteProps {
  children: ReactNode;
  /** Role gate mirroring aligned's AuthGuard/AdminGuard/OperationsGuard. */
  requireRole?: RequiredRole;
  /** Email-verification gate (aligned's startup gate for the expert flow):
   *  an unverified account is sent to /email-verification. */
  requireVerifiedEmail?: boolean;
  /** Onboarding gate (aligned's startup/home-layout re-check): an expert who
   *  hasn't completed the onboarding wizard is sent to /onboarding. */
  requireOnboarded?: boolean;
}

export function PrivateRoute({
  children,
  requireRole,
  requireVerifiedEmail,
  requireOnboarded,
}: PrivateRouteProps) {
  const {
    currentUser,
    loading,
    accountMissing,
    userAccount,
    isOwner,
    isFinanceAdmin,
    isExpert,
    isManager,
    isAdmin,
    isOperationsManager,
    isContractorAdmin,
  } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentUser) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={paths.public.login + '?redirect=' + redirect} replace />;
  }

  // Signed in to Firebase but no Aligned account doc → no access.
  if (accountMissing) {
    return <Navigate to={paths.public.noPermissions} replace />;
  }

  // An unqualified expert may still view their dashboard, but cannot enter any
  // other authenticated page. Run this before the role and onboarding gates so
  // those redirects cannot bounce the user away from the dashboard.
  if (userAccount?.status === 'unqualified') {
    return location.pathname === paths.expert.root
      ? <>{children}</>
      : <Navigate to={paths.expert.root} replace />;
  }

  if (requireRole && !hasRouteAccess(requireRole, { isOwner, isFinanceAdmin, isExpert, isManager, isAdmin, isOperationsManager, isContractorAdmin })) {
    return <Navigate to={paths.public.noPermissions} replace />;
  }

  if (requireVerifiedEmail && userAccount && !userAccount.isVerifiedEmail) {
    return <Navigate to={paths.public.emailVerification} replace />;
  }

  if (requireOnboarded && userAccount && !userAccount.raterOnboardingComplete) {
    return <Navigate to={paths.onboarding.root} replace />;
  }

  return <>{children}</>;
}
